import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { company, navigation } from '@/mocks/site';
import { services } from '@/mocks/services';
import BrandLogo from '@/components/feature/BrandLogo';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const compact = scrolled || menuOpen;

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50">
        {/* bandeau technique */}
        <div
          className={`hidden overflow-hidden bg-primary-950 transition-all duration-500 md:block ${
            compact ? 'max-h-0 opacity-0' : 'max-h-10 opacity-100'
          }`}
        >
          <div className="container-wide flex h-10 items-center justify-between border-b border-background-50/10">
            <div className="flex items-center gap-6">
              <span className="flex items-center gap-2 text-[11px] tracking-wide text-background-300">
                <i className="ri-map-pin-line text-accent-400" />
                {company.address}
              </span>
              <span className="hidden items-center gap-2 text-[11px] tracking-wide text-background-400 lg:flex">
                <i className="ri-time-line text-accent-400" />
                {company.hours}
              </span>
            </div>
            <div className="flex items-center gap-5">
              <a
                href={`mailto:${company.email}`}
                className="flex items-center gap-2 text-[11px] tracking-wide text-background-300 transition-colors hover:text-accent-400"
              >
                <i className="ri-mail-line text-accent-400" />
                {company.email}
              </a>
              <a
                href={company.phoneHref}
                className="flex items-center gap-2 text-[11px] font-medium tracking-wide text-background-50 transition-colors hover:text-accent-400"
              >
                <i className="ri-phone-line text-accent-400" />
                {company.phone}
              </a>
            </div>
          </div>
        </div>

        {/* barre principale */}
        <div
          className={`relative border-b bg-primary-950/95 backdrop-blur-md transition-colors duration-500 ${
            compact ? 'border-accent-500/60' : 'border-background-50/12'
          }`}
        >
          <div className="container-wide flex h-[74px] items-center justify-between gap-3 lg:h-[80px] lg:gap-4">
            <BrandLogo markSize={42} onDark taglineFrom="xl" className="flex-shrink-0" />

            <nav className="hidden items-center gap-3 lg:flex xl:gap-7">
              {navigation.map((item) => {
                const linkClass = ({ isActive }: { isActive: boolean }) =>
                  `link-underline label-mono pb-1 text-[10px] transition-colors duration-300 xl:text-[11px] ${
                    isActive
                      ? 'text-accent-400'
                      : 'text-background-200 hover:text-background-50'
                  }`;

                if (item.path === '/offres') {
                  return (
                    <div key={item.path} className="group relative">
                      <NavLink to={item.path} className={linkClass}>
                        <span className="flex items-center gap-1">
                          {item.label}
                          <i className="ri-arrow-down-s-line text-xs transition-transform duration-300 group-hover:rotate-180" />
                        </span>
                      </NavLink>
                      <div className="invisible absolute left-1/2 top-full w-[500px] -translate-x-1/2 pt-6 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
                        <div className="overflow-hidden rounded-lg bg-background-50 ring-1 ring-primary-900/10">
                          <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
                            <span className="label-mono text-primary-600">Nos 10 expertises</span>
                            <span className="text-[10px] uppercase tracking-[0.16em] text-foreground-400">
                              Atelier de Logbaba
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-0.5 p-2">
                            {services.map((service) => (
                              <Link
                                key={service.slug}
                                to="/offres"
                                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-xs text-foreground-700 transition-colors duration-200 hover:bg-background-100 hover:text-foreground-950"
                              >
                                <span className="font-label text-[10px] text-primary-500">
                                  {service.index}
                                </span>
                                <span className="truncate">{service.title}</span>
                              </Link>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    className={linkClass}
                  >
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>

            <div className="flex flex-shrink-0 items-center gap-2.5">
              <a
                href={company.phoneHref}
                className="hidden items-center gap-2 text-sm font-medium text-background-100 transition-colors hover:text-accent-400 xl:flex"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/25">
                  <i className="ri-phone-line text-accent-400" />
                </span>
                {company.phone}
              </a>
              <Link
                to="/contact"
                className="btn-shine hidden cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-[13px] font-semibold uppercase tracking-[0.08em] text-foreground-950 transition-colors duration-300 hover:bg-accent-400 sm:flex"
              >
                Demander un devis
                <i className="ri-arrow-right-up-line text-base" />
              </Link>
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
                aria-expanded={menuOpen}
                className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-md border border-background-50/25 text-background-50 transition-colors hover:border-accent-500 hover:text-accent-400 lg:hidden"
              >
                <i className={menuOpen ? 'ri-close-line text-2xl' : 'ri-menu-3-line text-2xl'} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* voile */}
      <div
        onClick={() => setMenuOpen(false)}
        className={`fixed inset-0 z-30 bg-foreground-950/60 backdrop-blur-sm transition-opacity duration-500 lg:hidden ${
          menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        aria-hidden="true"
      />

      {/* tiroir latéral */}
      <aside
        className={`fixed bottom-0 right-0 top-[74px] z-40 flex w-[86vw] max-w-[360px] flex-col overflow-hidden bg-primary-950 text-background-50 transition-transform duration-500 ease-smooth lg:hidden ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!menuOpen}
      >
        <div className="pointer-events-none absolute inset-0 hatch-lines-light opacity-30" />
        <div className="relative flex items-center justify-between border-b border-background-50/12 px-5 py-3.5">
          <span className="label-mono text-accent-400">Navigation</span>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label="Fermer le menu"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-50/25 text-background-100 transition-colors hover:border-accent-500 hover:text-accent-400"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <nav className="relative flex-1 overflow-y-auto px-5 py-2">
          {navigation.map((item, index) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                `flex cursor-pointer items-center justify-between border-b border-background-50/10 py-4 font-heading text-2xl uppercase tracking-wide transition-colors ${
                  isActive ? 'text-accent-400' : 'text-background-50 hover:text-accent-400'
                }`
              }
            >
              <span>{item.label}</span>
              <span className="font-label text-[10px] tracking-[0.18em] text-background-400">
                0{index + 1}
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="relative space-y-4 border-t border-background-50/12 bg-primary-950 p-5">
          <Link
            to="/contact"
            className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.1em] text-foreground-950 transition-colors hover:bg-accent-400"
          >
            Demander un devis
            <i className="ri-arrow-right-up-line text-sm" />
          </Link>
          <div className="space-y-2.5 text-xs text-background-300">
            <a href={company.phoneHref} className="flex items-center gap-2.5">
              <i className="ri-phone-line text-accent-400" />
              {company.phone}
            </a>
            <a href={`mailto:${company.email}`} className="flex items-center gap-2.5">
              <i className="ri-mail-line text-accent-400" />
              {company.email}
            </a>
            <p className="flex items-start gap-2.5">
              <i className="ri-map-pin-line mt-0.5 text-accent-400" />
              {company.address}
            </p>
          </div>
        </div>
      </aside>
    </>
  );
}